import moment from "moment";

/**
 * Removes the time portion from a date and returns it in "YYYY-MM-DD" format.
 *
 * @param {string|Date} date - The date to be formatted.
 * @returns {string|null} The formatted date in "YYYY-MM-DD" format, or null if the date is not provided.
 */
export const removeTimeFromDate = (date) =>
  date ? moment(date).format("YYYY-MM-DD") : null;

/**
 * Removes all non-numeric characters from a CPF string.
 *
 * @param {string} cpf - The CPF string to be cleaned.
 * @returns {string} The CPF string with only numeric characters.
 */
function removeNonNumeric(cpf) {
  return cpf.replace(/\D/g, "");
}

/**
 * Adds dots after the first three and second three digits in a CPF string.
 *
 * @param {string} cpf - The CPF string to be formatted.
 * @returns {string} The CPF string with dots added.
 */
function addFirstAndSecondDots(cpf) {
  return cpf.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2");
}

/**
 * Adds a hyphen before the last two digits in a CPF string.
 *
 * @param {string} cpf - The CPF string to be formatted.
 * @returns {string} The CPF string with a hyphen added.
 */
function addHyphen(cpf) {
  return cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

/**
 * Formats a CPF string by adding dots and a hyphen in the appropriate places.
 *
 * @param {string} cpf - The CPF string to be formatted.
 * @returns {string} The formatted CPF string.
 */
export function formatCpf(cpf) {
  const numericCpf = removeNonNumeric(cpf);
  const cpfWithDots = addFirstAndSecondDots(numericCpf);
  const formattedCpf = addHyphen(cpfWithDots);

  return formattedCpf;
}

/**
 * Formats a CNPJ string by adding dots and a slash in the appropriate places.
 *
 * @param {string} cnpj - The CNPJ string to be formatted.
 * @returns {string} The formatted CNPJ string.
 */
export function formatCnpj(cnpj) {
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}
